import { diaNegocio, formatearMoneda, sumarDias, type NominaPersona, type NominaSemana } from '@barbones/shared';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, CircleCheck, Wallet } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Tabla, Tarjeta, Td, Th, Tr, Vacio } from '@/components/ui/data';
import { diaLargo } from '@/lib/utils';
import { PagoDialog } from './PagoDialog';

const horas = (min: number) => `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')} m`;

export function NominaPage() {
  const { puede } = useSesion();
  const [dia, setDia] = useState(diaNegocio(new Date()));
  const [pagar, setPagar] = useState<NominaPersona | null>(null);
  const q = useQuery({ queryKey: ['nomina', dia], queryFn: () => api<NominaSemana>('/nomina/semana', { query: { semana: dia } }) });
  const d = q.data;
  const totalPendiente = d?.personas.filter((p) => !p.pagado).reduce((s, p) => s + p.calculo.total, 0) ?? 0;

  return (
    <ModuleLayout titulo="Personal" secciones={SECCIONES_POR_MODULO.personal}>
      <EncabezadoPagina
        titulo="Pago semanal"
        descripcion="Sueldo (ajustado por tiempo trabajado), comisiones por servicios y productos, propinas y faltantes de caja."
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variante="secundario" tamano="icono" onClick={() => setDia(sumarDias(d?.semana ?? dia, -7))} aria-label="Semana anterior">
          <ChevronLeft />
        </Button>
        <p className="min-w-64 text-center text-[14px] font-medium first-letter:uppercase">{d ? `${diaLargo(d.semana)} – ${diaLargo(d.hasta)}` : '…'}</p>
        <Button variante="secundario" tamano="icono" onClick={() => setDia(sumarDias(d?.semana ?? dia, 7))} aria-label="Semana siguiente">
          <ChevronRight />
        </Button>
        {d && (
          <span className="cifras ml-auto text-[13.5px] text-foreground-muted">
            Pendiente de pagar: <strong className="text-foreground">{formatearMoneda(totalPendiente)}</strong>
          </span>
        )}
      </div>
      <Tarjeta>
        {!d ? (
          <Esqueleto className="m-4 h-40" />
        ) : d.personas.length === 0 ? (
          <Vacio icono={<Wallet />} titulo="Sin pagos esta semana" />
        ) : (
          <Tabla>
            <thead>
              <tr>
                <Th>Persona</Th>
                <Th className="text-right">Tiempo trabajado</Th>
                <Th className="text-right">Sueldo</Th>
                <Th className="text-right">Comisiones</Th>
                <Th className="text-right">Propinas</Th>
                <Th className="text-right">Faltantes</Th>
                <Th className="text-right">Total</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {d.personas.map((p) => (
                <Tr key={p.usuario.id}>
                  <Td>
                    <p className="font-medium">{p.usuario.nombre}</p>
                    <p className="text-[12px] text-foreground-muted">
                      {p.servicios} servicios · {p.retardos} retardos · {p.faltas} faltas
                    </p>
                  </Td>
                  <Td className="cifras text-right text-[13px]">
                    {horas(p.minutosTrabajados)}
                    {p.minutosProgramados > 0 && <span className="block text-foreground-muted">de {horas(p.minutosProgramados)}</span>}
                  </Td>
                  <Td className="cifras text-right">{p.sueldoSemanal != null ? formatearMoneda(p.calculo.sueldoBase + p.calculo.ajusteTiempo) : '—'}</Td>
                  <Td className="cifras text-right">{formatearMoneda(p.comisionServicios + p.comisionProductos)}</Td>
                  <Td className="cifras text-right">{formatearMoneda(p.propinasDirectas + p.propinasReparto)}</Td>
                  <Td className="cifras text-right">
                    {p.faltantesPendientes.length ? (
                      <span className="text-destructive">{formatearMoneda(p.faltantesPendientes.reduce((s, f) => s + f.montoMXN, 0))}</span>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td className="cifras text-right font-display text-[16px]">{formatearMoneda(p.pagado?.total ?? p.calculo.total)}</Td>
                  <Td className="text-right">
                    {p.pagado ? (
                      <Insignia tono="exito">
                        <CircleCheck /> Pagado
                      </Insignia>
                    ) : (
                      puede('nomina.pagar') && (
                        <Button tamano="sm" onClick={() => setPagar(p)}>
                          Pagar
                        </Button>
                      )
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Tabla>
        )}
      </Tarjeta>
      {pagar && d && <PagoDialog persona={pagar} semana={d.semana} onCerrar={() => setPagar(null)} />}
    </ModuleLayout>
  );
}
